import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import MainLayoutApple from '../components/apple-ui/MainLayoutApple';
import { useTheme } from '@/contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  translation: { label: 'Translation', color: 'bg-blue-500' },
  cat_ai: { label: 'CAT Tool', color: 'bg-indigo-500' },
  cat_tool: { label: 'CAT Tool', color: 'bg-indigo-500' },
  mtpe: { label: 'MTPE', color: 'bg-green-500' },
  mtpe_basic: { label: 'MTPE', color: 'bg-green-500' },
  mtpe_advanced: { label: 'MTPE Pro', color: 'bg-emerald-500' },
  mtpe_pro: { label: 'MTPE Pro', color: 'bg-emerald-500' },
  tqa: { label: 'TQA', color: 'bg-purple-500' },
  translation_qa: { label: 'Translation QA', color: 'bg-violet-500' },
  review: { label: 'Super Review', color: 'bg-rose-500' },
  super_review: { label: 'Super Review', color: 'bg-rose-500' },
  proofreading: { label: 'Proofreading', color: 'bg-orange-500' },
  proofread: { label: 'Proofreading', color: 'bg-orange-500' },
};

function mergeFeatures(usageByFeature) {
  const merged = {};
  for (const item of usageByFeature) {
    const key = item._id;
    const config = FEATURE_LABELS[key];
    if (!config) continue;
    const mergeKey = config.label;
    if (!merged[mergeKey]) {
      merged[mergeKey] = { label: mergeKey, color: config.color, words: 0 };
    }
    merged[mergeKey].words += item.total_words || 0;
  }
  return Object.values(merged).sort((a, b) => b.words - a.words);
}

export default function UsagePage(props) {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/pricing/usage`, { headers });
      setData(res.data);
    } catch {
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const trackBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-100';

  const plan = data?.plan || 'free';
  const isPayg = plan === 'payg';
  const wordsUsed = data?.monthly_usage || 0;
  const walletWords = data?.wallet_words || 0;
  // PAYG: quota = wallet_words remaining + used (total wallet at topup); subscription: monthly_quota
  const wordsQuota = isPayg ? (walletWords + wordsUsed) : (data?.monthly_quota || 1000);
  const usagePercent = wordsQuota > 0 ? Math.min(100, Math.round((wordsUsed / wordsQuota) * 100)) : 0;
  const features = data ? mergeFeatures(data.usage_by_feature || []) : [];
  const maxWords = features.length > 0 ? features[0].words : 1;

  return (
    <MainLayoutApple {...props}>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Usage</h1>
          <p className={`text-sm mt-1 ${textSecondary}`}>Words used this month by feature</p>
        </div>

        {/* Total summary */}
        <Card className={`p-6 border ${cardBg}`}>
          {loading ? (
            <div className={`text-sm ${textSecondary}`}>Loading...</div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>{isPayg ? 'Words used from wallet this month' : 'Total words this month'}</span>
                <span className={`font-semibold ${textPrimary}`}>
                  {wordsUsed.toLocaleString()} / {wordsQuota === 999999999 ? '∞' : wordsQuota.toLocaleString()}
                </span>
              </div>
              <div className={`h-2.5 rounded-full ${trackBg}`}>
                <div
                  className={`h-2.5 rounded-full transition-all ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              <div className={`text-xs ${textSecondary}`}>
                {isPayg ? `${walletWords.toLocaleString()} wallet words remaining` : `${usagePercent}% of monthly quota used`}
              </div>
            </div>
          )}
        </Card>

        {/* Per-feature breakdown */}
        <Card className={`p-6 border ${cardBg}`}>
          <h2 className={`font-semibold mb-5 ${textPrimary}`}>Breakdown by Feature</h2>
          {loading ? (
            <div className={`text-sm ${textSecondary}`}>Loading...</div>
          ) : features.length === 0 ? (
            <div className={`text-sm ${textSecondary} text-center py-6`}>No usage recorded this month</div>
          ) : (
            <div className="space-y-4">
              {features.map((f) => {
                const pct = maxWords > 0 ? Math.round((f.words / maxWords) * 100) : 0;
                return (
                  <div key={f.label} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className={textPrimary}>{f.label}</span>
                      <span className={`font-medium ${textPrimary}`}>{f.words.toLocaleString()} words</span>
                    </div>
                    <div className={`h-2 rounded-full ${trackBg}`}>
                      <div
                        className={`h-2 rounded-full transition-all ${f.color}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </MainLayoutApple>
  );
}
